// The ISS model in as few draw calls as it has materials, and drawn only
// while some of it is inside the view.
//
// NASA's model is 343 meshes on 29 materials: about 340 draw calls and 248k
// triangles a frame. Its meshes had frustum culling off, so every view paid
// for it, Saturn or the Sun framed as much as the Earth, and with the station
// far under a pixel: 80% of the viewer's draw calls (scripts/test-perf.mjs,
// docs/performance.md). Merged it is 29 calls when it is drawn, and none when
// it is out of view. However small it is on screen it is still drawn: under
// a pixel it can still light one (a sunlit speck, 23 of 255 at 0.8 px).
import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

// A mirrored transform baked into the vertices turns the faces inside out;
// reversing each triangle's order turns them back.
function flipWinding(geometry) {
  const index = geometry.index;
  if (!index) return;
  const a = index.array;
  for (let i = 0; i < a.length; i += 3) {
    const t = a[i + 1];
    a[i + 1] = a[i + 2];
    a[i + 2] = t;
  }
  index.needsUpdate = true;
}

// One mesh per material (and vertex layout), its parts' transforms baked into
// the vertices in root's own frame. Meshes that cannot be merged (several
// materials, skinning, morphs, instancing) are kept as they were. Returns a
// new group to use in root's place; root's geometries are disposed, its
// materials and maps are reused.
export function mergeByMaterial(root) {
  root.updateMatrixWorld(true);
  const toRoot = new THREE.Matrix4().copy(root.matrixWorld).invert();
  const local = (o) => new THREE.Matrix4().multiplyMatrices(toRoot, o.matrixWorld);
  const groups = new Map();
  const kept = [];
  const geometries = new Set();
  root.traverse((o) => {
    if (!o.isMesh) return;
    if (Array.isArray(o.material) || o.isSkinnedMesh || o.isInstancedMesh || o.morphTargetInfluences) {
      kept.push(o);
      return;
    }
    geometries.add(o.geometry);
    const layout = Object.keys(o.geometry.attributes).sort().join(',') + (o.geometry.index ? '+index' : '');
    const key = `${o.material.uuid}|${layout}`;
    if (!groups.has(key)) groups.set(key, { material: o.material, meshes: [] });
    groups.get(key).meshes.push(o);
  });

  const out = new THREE.Group();
  out.name = root.name;
  const addMesh = (geometry, material, name) => {
    const mesh = new THREE.Mesh(geometry, material);
    mesh.name = name;
    out.add(mesh);
  };
  for (const { material, meshes } of groups.values()) {
    const baked = meshes.map((m) => {
      const g = m.geometry.clone();
      const matrix = local(m);
      g.applyMatrix4(matrix);
      if (matrix.determinant() < 0) flipWinding(g);
      return g;
    });
    const merged = baked.length > 1 ? mergeGeometries(baked, false) : baked[0];
    if (merged) {
      if (merged !== baked[0]) baked.forEach((g) => g.dispose());
      merged.computeBoundingSphere();
      addMesh(merged, material, material.name || meshes[0].name);
    } else {
      baked.forEach((g, i) => addMesh(g, material, meshes[i].name));
    }
  }
  for (const o of kept) {
    // Without its children: any meshes under it are merged above.
    const copy = o.clone(false);
    local(o).decompose(copy.position, copy.quaternion, copy.scale);
    out.add(copy);
    geometries.delete(o.geometry);
  }
  geometries.forEach((g) => g.dispose());
  return out;
}

const _sphere = new THREE.Sphere();
const _frustum = new THREE.Frustum();
const _viewProjection = new THREE.Matrix4();

// Whether any of a model of the given length, centred at `center` (world
// space), can be inside this camera's view: a sphere of 0.75 of its length
// holds the station whichever way it is turned (its half-diagonal is 0.61).
// The camera's matrices must be current.
export function modelInView({ center, length, camera }) {
  _viewProjection.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
  _frustum.setFromProjectionMatrix(_viewProjection);
  _sphere.center.copy(center);
  _sphere.radius = length * 0.75;
  return _frustum.intersectsSphere(_sphere);
}
